import { useCallback, useEffect, useMemo, useState } from "react";
import { AppLayout } from "@/components/AppLayout";
import { RoleGuard } from "@/components/RoleGuard";
import { PageHeader, SegmentedControl, SelectBusca } from "@/components/padroes";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EditarCadastroModal } from "@/components/EditarCadastroModal";
import { AdicionarUsuarioModal } from "@/components/AdicionarUsuarioModal";
import { ImportarUsuariosModal } from "@/components/ImportarUsuariosModal";
import { ImportarAdmissoesModal } from "@/components/ImportarAdmissoesModal";
import { DadosCltModal } from "@/components/clt/DadosCltModal";
import { BeneficiosColaboradorModal } from "@/components/beneficios/BeneficiosColaboradorModal";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Paginacao } from "@/components/Paginacao";
import { useAuth } from "@/context/AuthContext";
import { useDebounce, usePaginado } from "@/hooks/usePaginado";
import { atualizarContadoresSidebar } from "@/hooks/useContadoresSidebar";
import { api } from "@/lib/api";
import { formatBRL, formatCNPJ, formatCPF } from "@/lib/format";
import { cn } from "@/lib/utils";
import type { User, Tomador } from "@/data/mock";
import type { Headcount } from "@/data/dashboard";
import { toast } from "sonner";
import {
  UserX,
  UserCheck,
  Loader2,
  Pencil,
  UserPlus,
  FileSpreadsheet,
  Briefcase,
  Gift,
  Search,
  CalendarDays,
  CloudDownload,
  ChevronDown,
  MoreHorizontal,
} from "lucide-react";

const SEM_TOMADOR = "__none__";
const TODOS = "__todos__";
const MAX_SELECT = 5;
const PAGE_SIZE = 25;

/** Guardado com o nome para a seleção sobreviver à troca de página. */
type Alvo = { id: string; name: string };

type Filtro = "todos" | "pj" | "clt" | "incompleto";

const FILTROS: { value: Filtro; label: string }[] = [
  { value: "todos", label: "Todos" },
  { value: "pj", label: "PJ" },
  { value: "clt", label: "CLT" },
  { value: "incompleto", label: "Cadastro incompleto" },
];

const AMBAR = "text-amber-700 dark:text-amber-400";

const iniciais = (nome: string) =>
  nome
    .split(/\s+/)
    .filter(Boolean)
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

const temCpf = (u: User) => (u.cpf ?? "").replace(/\D/g, "").length === 11;
const temCnpj = (u: User) => (u.cnpj ?? "").replace(/\D/g, "").length === 14;

const RHColaboradores = () => {
  const { user: me } = useAuth();
  const [busca, setBusca] = useState("");
  const [filtro, setFiltro] = useState<Filtro>("todos");
  const [status, setStatus] = useState(TODOS);
  const buscaAtrasada = useDebounce(busca.trim());
  const incompletos = filtro === "incompleto";
  const {
    items: paginaUsuarios,
    total: totalPaginado,
    page: pagePaginado,
    carregando: carregandoPaginado,
    setPage: setPagePaginado,
    recarregar: recarregarPaginado,
  } = usePaginado<User>(
    "/colaboradores",
    {
      q: buscaAtrasada,
      vinculo: filtro === "pj" || filtro === "clt" ? filtro : undefined,
      ativo: status === TODOS ? undefined : status,
    },
    { pageSize: PAGE_SIZE, habilitado: !incompletos },
  );

  /**
   * Pendências de cadastro por usuário — a mesma regra do contador da sidebar
   * e do Headcount (só pessoas ativas). A listagem não filtra por pendência,
   * então o filtro "Cadastro incompleto" cruza com a lista completa no cliente.
   */
  const [pendencias, setPendencias] = useState<Map<string, string[]>>(new Map());
  const [todosAtivos, setTodosAtivos] = useState<User[] | null>(null);
  const [pageIncompletos, setPageIncompletos] = useState(1);

  const carregarPendencias = useCallback(async () => {
    try {
      const h = await api.get<Headcount>("/dashboard/headcount");
      setPendencias(new Map(h.pendencias.map((p) => [p.id, p.pendencias])));
    } catch {
      // Sem pendências a tabela só não marca "Incompleto".
    }
  }, []);

  const carregarIncompletos = useCallback(async () => {
    setTodosAtivos(null);
    try {
      const qs = new URLSearchParams({ ativo: "true" });
      if (buscaAtrasada) qs.set("q", buscaAtrasada);
      setTodosAtivos(await api.get<User[]>(`/colaboradores?${qs}`));
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao carregar a lista");
      setTodosAtivos([]);
    }
  }, [buscaAtrasada]);

  useEffect(() => {
    void carregarPendencias();
  }, [carregarPendencias]);

  useEffect(() => {
    if (!incompletos) return;
    setPageIncompletos(1);
    void carregarIncompletos();
  }, [incompletos, carregarIncompletos]);

  const listaIncompletos = useMemo(
    () => (todosAtivos ?? []).filter((u) => pendencias.has(u.id)),
    [todosAtivos, pendencias],
  );

  const usuarios = incompletos
    ? listaIncompletos.slice((pageIncompletos - 1) * PAGE_SIZE, pageIncompletos * PAGE_SIZE)
    : paginaUsuarios;
  const total = incompletos ? listaIncompletos.length : totalPaginado;
  const page = incompletos ? pageIncompletos : pagePaginado;
  const setPage = incompletos ? setPageIncompletos : setPagePaginado;
  const carregando = incompletos ? todosAtivos === null : carregandoPaginado;

  /** Depois de qualquer mudança de cadastro: lista, pendências e contador da sidebar. */
  const recarregar = useCallback(async () => {
    atualizarContadoresSidebar();
    await Promise.all([
      incompletos ? carregarIncompletos() : recarregarPaginado(),
      carregarPendencias(),
    ]);
  }, [incompletos, carregarIncompletos, recarregarPaginado, carregarPendencias]);

  // Lista completa: select "Tomador vinculado" de cada linha e modal de adicionar.
  const [tomadores, setTomadores] = useState<Tomador[]>([]);
  const [linking, setLinking] = useState<string | null>(null);
  const [adicionarModal, setAdicionarModal] = useState(false);
  const [importarModal, setImportarModal] = useState(false);
  const [admissoesModal, setAdmissoesModal] = useState(false);

  const [selected, setSelected] = useState<Alvo[]>([]);
  /** Usuários alvo do modal de inativação (lote ou individual). Vazio = modal fechado. */
  const [inativarAlvos, setInativarAlvos] = useState<Alvo[]>([]);
  const [inativarConfirm, setInativarConfirm] = useState("");
  const [inativando, setInativando] = useState(false);
  const [reativando, setReativando] = useState<string | null>(null);
  const [editandoCadastro, setEditandoCadastro] = useState<User | null>(null);
  const [dadosClt, setDadosClt] = useState<User | null>(null);
  const [beneficiosDe, setBeneficiosDe] = useState<User | null>(null);

  useEffect(() => {
    api.get<Tomador[]>("/tomadores").then(setTomadores).catch(() => {});
  }, []);

  const handleImportWorkspace = () => {
    const token = localStorage.getItem("nf_app_token");
    if (!token) { toast.error("Sessão expirada. Faça login novamente."); return; }
    const apiUrl = import.meta.env.VITE_API_URL ?? "http://localhost:3333/api/v1";
    window.location.href = `${apiUrl}/auth/google/workspace?jwt=${encodeURIComponent(token)}`;
  };

  const handleVincular = async (userId: string, tomadorId: string) => {
    setLinking(userId);
    try {
      await api.patch(`/colaboradores/${userId}/tomador`, {
        tomadorId: tomadorId === SEM_TOMADOR ? null : tomadorId,
      });
      toast.success("Vínculo atualizado");
      await recarregar();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao vincular");
    } finally {
      setLinking(null);
    }
  };

  const toggleSelect = (u: User) => {
    setSelected((prev) => {
      if (prev.some((s) => s.id === u.id)) return prev.filter((s) => s.id !== u.id);
      if (prev.length >= MAX_SELECT) return prev;
      return [...prev, { id: u.id, name: u.name }];
    });
  };

  const abrirInativar = (alvos: Alvo[]) => {
    setInativarConfirm("");
    setInativarAlvos(alvos);
  };

  const handleInativar = async () => {
    if (inativarConfirm !== "Sim") return;
    setInativando(true);
    const ids = inativarAlvos.map((a) => a.id);
    const resultados = await Promise.allSettled(
      ids.map((id) => api.patch<User>(`/colaboradores/${id}/ativo`, { ativo: false })),
    );

    const ok = resultados.filter((r) => r.status === "fulfilled").length;
    const falhas = resultados.length - ok;
    if (ok > 0) toast.success(`${ok} usuário(s) inativado(s) com sucesso`);
    if (falhas > 0) {
      const erro = resultados.find((r): r is PromiseRejectedResult => r.status === "rejected")?.reason;
      toast.error(`Falha ao inativar ${falhas} usuário(s)${erro instanceof Error ? `: ${erro.message}` : ""}`);
    }

    setSelected((prev) => prev.filter((s) => !ids.includes(s.id)));
    setInativarAlvos([]);
    setInativando(false);
    if (ok > 0) await recarregar();
  };

  const handleReativar = async (id: string) => {
    setReativando(id);
    try {
      await api.patch<User>(`/colaboradores/${id}/ativo`, { ativo: true });
      toast.success("Usuário reativado");
      await recarregar();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao reativar usuário");
    } finally {
      setReativando(null);
    }
  };

  const subtitulo = carregando && total === 0
    ? "Carregando…"
    : [
        `${total} ${incompletos ? "com cadastro incompleto" : total === 1 ? "usuário" : "usuários"}`,
        !incompletos && pendencias.size > 0 && `${pendencias.size} com cadastro incompleto`,
      ]
        .filter(Boolean)
        .join(" · ");

  return (
    <RoleGuard role="rh">
      <AppLayout title="Usuários">
        <div className="mx-auto flex max-w-7xl flex-col gap-4">
          <PageHeader
            titulo="Usuários"
            subtitulo={subtitulo}
            acoes={
              <>
                {me?.workspaceAdmin && (
                  <Button variant="outline" onClick={handleImportWorkspace}>
                    <CloudDownload />
                    Importar do Workspace
                  </Button>
                )}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline">
                      <FileSpreadsheet />
                      Importar planilha
                      <ChevronDown className="text-muted-foreground" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuItem onSelect={() => setImportarModal(true)}>
                      <FileSpreadsheet className="mr-2 h-4 w-4" />
                      Cadastro de usuários
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => setAdmissoesModal(true)}>
                      <CalendarDays className="mr-2 h-4 w-4" />
                      Datas de admissão
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                <Button onClick={() => setAdicionarModal(true)}>
                  <UserPlus />
                  Adicionar usuário
                </Button>
              </>
            }
          />

          <AdicionarUsuarioModal
            open={adicionarModal}
            onClose={() => setAdicionarModal(false)}
            tomadores={tomadores}
            onSuccess={() => void recarregar()}
          />

          <ImportarUsuariosModal
            open={importarModal}
            onClose={() => setImportarModal(false)}
            onSuccess={() => void recarregar()}
          />

          <ImportarAdmissoesModal
            open={admissoesModal}
            onClose={() => setAdmissoesModal(false)}
            onSuccess={() => void recarregar()}
          />

          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2">
              <SegmentedControl value={filtro} onChange={setFiltro} options={FILTROS} aria-label="Filtrar usuários" />
              {!incompletos && (
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger className="h-9 w-[150px]" aria-label="Filtrar por status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={TODOS}>Todos os status</SelectItem>
                    <SelectItem value="true">Ativos</SelectItem>
                    <SelectItem value="false">Inativos</SelectItem>
                  </SelectContent>
                </Select>
              )}
              {selected.length > 0 && (
                <Button variant="destructive" size="sm" onClick={() => abrirInativar(selected)}>
                  <UserX />
                  Inativar selecionados ({selected.length})
                </Button>
              )}
            </div>
            <div className="relative w-full sm:w-[340px]">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
                placeholder="Buscar por nome, e-mail ou CPF"
                className="h-[38px] bg-card pl-9"
                aria-label="Buscar usuários"
              />
            </div>
          </div>

          <section className="overflow-hidden rounded-xl border bg-card">
            <Table className="min-w-[1080px]">
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10" />
                  <TableHead>Colaborador</TableHead>
                  <TableHead>Documentos</TableHead>
                  <TableHead>Perfil / Departamento</TableHead>
                  <TableHead className="text-right">Salário</TableHead>
                  <TableHead>Tomador vinculado</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="w-10" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {usuarios.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="py-12 text-center text-muted-foreground">
                      {carregando
                        ? "Carregando..."
                        : incompletos
                          ? "Nenhum cadastro incompleto"
                          : "Nenhum usuário encontrado"}
                    </TableCell>
                  </TableRow>
                ) : (
                  usuarios.map((u) => {
                    const isMe = u.id === me?.id;
                    const isChecked = selected.some((s) => s.id === u.id);
                    const inativo = u.ativo === false;
                    const disableCheck = isMe || inativo || (!isChecked && selected.length >= MAX_SELECT);
                    const faltando = pendencias.get(u.id);
                    const clt = u.vinculo === "clt";

                    return (
                      <TableRow
                        key={u.id}
                        className={cn(isMe && "bg-primary/5", inativo && "opacity-60")}
                      >
                        <TableCell>
                          <Checkbox
                            checked={isChecked}
                            disabled={disableCheck}
                            onCheckedChange={() => toggleSelect(u)}
                            aria-label={`Selecionar ${u.name}`}
                          />
                        </TableCell>
                        <TableCell className="max-w-[300px] py-2.5">
                          <div className="flex min-w-0 items-center gap-2.5">
                            {u.avatarUrl ? (
                              <img src={u.avatarUrl} alt="" className="h-8 w-8 shrink-0 rounded-full object-cover" />
                            ) : (
                              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-foreground text-[11px] font-bold text-background">
                                {iniciais(u.name)}
                              </span>
                            )}
                            <div className="flex min-w-0 flex-col gap-px">
                              <div className="flex min-w-0 items-center gap-1.5">
                                <button
                                  type="button"
                                  onClick={() => setEditandoCadastro(u)}
                                  className="truncate text-left font-medium hover:underline hover:underline-offset-4"
                                  title={`Editar cadastro de ${u.name}`}
                                >
                                  {u.name}
                                </button>
                                <span
                                  className={cn(
                                    "shrink-0 rounded px-1.5 py-px text-[10px] font-bold",
                                    clt ? "bg-[rgb(235_104_52/0.12)] text-[#b9481c]" : "bg-[rgb(42_120_214/0.12)] text-[#1f5fae]",
                                  )}
                                >
                                  {clt ? "CLT" : "PJ"}
                                </span>
                                {isMe && (
                                  <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wider text-primary">
                                    você
                                  </span>
                                )}
                              </div>
                              <span className="truncate text-xs text-muted-foreground" title={u.email}>
                                {u.email}
                              </span>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-xs">
                          <div className={cn(!temCpf(u) && AMBAR)}>
                            {temCpf(u) ? `CPF ${formatCPF(u.cpf)}` : "CPF não informado"}
                          </div>
                          {!clt && (
                            <div className={cn("mt-px", temCnpj(u) ? "text-muted-foreground" : AMBAR)}>
                              {temCnpj(u) ? formatCNPJ(u.cnpj) : "CNPJ não informado"}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          <div>{u.role === "rh" ? "RH" : u.role === "financeiro" ? "Financeiro" : "Colaborador"}</div>
                          <div className={cn("mt-px text-xs", u.department ? "text-muted-foreground" : AMBAR)}>
                            {u.department ?? "Sem departamento"}
                          </div>
                        </TableCell>
                        <TableCell className="whitespace-nowrap text-right">
                          {u.salarioTotal != null ? (
                            <>
                              <span className="font-medium">{formatBRL(u.salarioTotal)}</span>
                              {!clt && (u.complementoSalario ?? 0) > 0 && (
                                <div className="text-[11px] text-muted-foreground">
                                  {formatBRL(u.salarioFixo ?? 0)} + {formatBRL(u.complementoSalario ?? 0)} compl.
                                </div>
                              )}
                            </>
                          ) : (
                            <span className={AMBAR}>Não informado</span>
                          )}
                        </TableCell>
                        <TableCell className="w-[220px]">
                          <SelectBusca
                            value={u.tomadorId ?? SEM_TOMADOR}
                            onChange={(v) => handleVincular(u.id, v)}
                            disabled={linking === u.id}
                            placeholder="Não vinculado"
                            placeholderBusca="Buscar tomador..."
                            className={cn(
                              "h-8 w-[210px] border-transparent bg-transparent px-2 text-[13px] shadow-none hover:border-input",
                              !u.tomadorId && AMBAR,
                            )}
                            aria-label={`Tomador de ${u.name}`}
                            opcoes={[
                              { value: SEM_TOMADOR, label: "Não vinculado", neutra: true },
                              ...tomadores.map((t) => ({ value: t.id, label: t.razaoSocial })),
                            ]}
                          />
                          {u.cnpjContratante && (
                            <div className="px-2 text-[11px] text-muted-foreground">
                              CNPJ {formatCNPJ(u.cnpjContratante)}
                            </div>
                          )}
                        </TableCell>
                        <TableCell>
                          {inativo ? (
                            <span className="inline-flex rounded-full bg-muted px-2.5 py-0.5 text-xs font-semibold text-muted-foreground">
                              Inativo
                            </span>
                          ) : faltando ? (
                            <span
                              className="inline-flex rounded-full bg-amber-500/15 px-2.5 py-0.5 text-xs font-semibold text-amber-700 dark:text-amber-400"
                              title={faltando.join(" · ")}
                            >
                              Incompleto
                            </span>
                          ) : (
                            <span className="inline-flex rounded-full bg-success/15 px-2.5 py-0.5 text-xs font-semibold text-success">
                              Ativo
                            </span>
                          )}
                          {faltando && !inativo && (
                            <div className={cn("mt-1 max-w-[160px] text-[11px] leading-tight", AMBAR)}>
                              {faltando.join(" · ")}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="px-2">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-muted-foreground"
                                aria-label={`Ações de ${u.name}`}
                              >
                                {reativando === u.id ? <Loader2 className="animate-spin" /> : <MoreHorizontal />}
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-52">
                              <DropdownMenuItem onSelect={() => setEditandoCadastro(u)}>
                                <Pencil className="mr-2 h-4 w-4" />
                                Editar cadastro
                              </DropdownMenuItem>
                              {clt && (
                                <DropdownMenuItem onSelect={() => setDadosClt(u)}>
                                  <Briefcase className="mr-2 h-4 w-4" />
                                  Dados do contrato CLT
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem onSelect={() => setBeneficiosDe(u)}>
                                <Gift className="mr-2 h-4 w-4" />
                                Benefícios
                              </DropdownMenuItem>
                              <DropdownMenuSeparator />
                              {inativo ? (
                                <DropdownMenuItem
                                  disabled={reativando === u.id}
                                  onSelect={() => void handleReativar(u.id)}
                                >
                                  <UserCheck className="mr-2 h-4 w-4" />
                                  Reativar
                                </DropdownMenuItem>
                              ) : (
                                <DropdownMenuItem
                                  disabled={isMe}
                                  className="text-destructive focus:text-destructive"
                                  onSelect={() => abrirInativar([{ id: u.id, name: u.name }])}
                                >
                                  <UserX className="mr-2 h-4 w-4" />
                                  Inativar
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </section>

          <Paginacao
            page={page}
            pageSize={PAGE_SIZE}
            total={total}
            onPage={setPage}
            carregando={carregando}
            rotulo="usuários"
          />

          {selected.length > 0 && (
            <p className="text-xs text-muted-foreground">
              {selected.length} de {MAX_SELECT} usuários selecionados
            </p>
          )}
        </div>
      </AppLayout>

      <AlertDialog
        open={inativarAlvos.length > 0}
        onOpenChange={(open) => !open && !inativando && setInativarAlvos([])}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Inativar {inativarAlvos.length > 1 ? "usuários" : "usuário"}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3">
                <p>
                  Você está prestes a inativar <strong>{inativarAlvos.length} usuário(s)</strong>:
                </p>
                <ul className="list-disc space-y-0.5 pl-5 text-sm text-foreground">
                  {inativarAlvos.map((a) => <li key={a.id}>{a.name}</li>)}
                </ul>
                <p className="font-medium text-destructive">
                  Usuários inativos deixam de entrar na folha de pagamento. Os dados são mantidos e é possível reativá-los a qualquer momento.
                </p>
                <div className="space-y-1.5 pt-1">
                  <p className="text-sm text-foreground">Digite <strong>Sim</strong> para confirmar:</p>
                  <Input
                    value={inativarConfirm}
                    onChange={(e) => setInativarConfirm(e.target.value)}
                    placeholder="Sim"
                    autoFocus
                  />
                </div>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={inativando}>Cancelar</AlertDialogCancel>
            <Button
              variant="destructive"
              disabled={inativarConfirm !== "Sim" || inativando}
              onClick={handleInativar}
            >
              {inativando && <Loader2 className="animate-spin" />}
              Inativar
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <BeneficiosColaboradorModal usuario={beneficiosDe} onClose={() => setBeneficiosDe(null)} />
      {dadosClt && (
        <DadosCltModal
          usuario={dadosClt}
          open={!!dadosClt}
          onClose={() => {
            setDadosClt(null);
            // A admissão do CLT vem do contrato: pode ter resolvido uma pendência.
            atualizarContadoresSidebar();
            void carregarPendencias();
          }}
        />
      )}

      {editandoCadastro && (
        <EditarCadastroModal
          usuario={editandoCadastro}
          open={!!editandoCadastro}
          onClose={() => setEditandoCadastro(null)}
          onSuccess={() => void recarregar()}
        />
      )}
    </RoleGuard>
  );
};

export default RHColaboradores;
